import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';
import {
  PlusIcon,
  EyeIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  DocumentArrowUpIcon
} from '@heroicons/react/24/outline';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

const Proposals = () => {
  const { token, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (token) {
      fetchProposals();
    }
  }, [statusFilter, token]);

  const fetchProposals = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (statusFilter !== 'all') {
        params.append('status', statusFilter);
      }

      const response = await fetch(`${API_URL}/proposals?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) throw new Error('Failed to fetch proposals');
      const data = await response.json();
      setProposals(data.proposals || []);
    } catch (error) {
      console.error('Error fetching proposals:', error);
      toast.error('Failed to load proposals');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      draft: { bg: '#f3f4f6', color: '#6b7280', icon: ClockIcon },
      under_review: { bg: '#fef3c7', color: '#92400e', icon: ClockIcon },
      approved: { bg: '#d1fae5', color: '#065f46', icon: CheckCircleIcon },
      rejected: { bg: '#fee2e2', color: '#991b1b', icon: XCircleIcon },
      converted: { bg: '#dbeafe', color: '#1e40af', icon: DocumentArrowUpIcon }
    };

    const style = styles[status] || styles.draft;
    const Icon = style.icon;

    return (
      <span style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        padding: '4px 12px',
        borderRadius: '12px',
        fontSize: '12px',
        fontWeight: '500',
        background: style.bg,
        color: style.color
      }}>
        <Icon style={{ width: 14, height: 14 }} />
        {status.replace('_', ' ').toUpperCase()}
      </span>
    );
  };

  const filteredProposals = proposals.filter(proposal =>
    proposal.client_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    proposal.project_description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const statusCounts = {
    all: proposals.length,
    draft: proposals.filter(p => p.status === 'draft').length,
    under_review: proposals.filter(p => p.status === 'under_review').length,
    approved: proposals.filter(p => p.status === 'approved').length,
    rejected: proposals.filter(p => p.status === 'rejected').length,
    converted: proposals.filter(p => p.status === 'converted').length
  };

  return (
    <div style={{ padding: '24px', background: '#f9fafb', minHeight: 'calc(100vh - 48px)' }}>
      {/* Header */}
      <div style={{ marginBottom: '32px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
        <div>
          <h1 style={{ fontSize: '32px', fontWeight: '600', color: '#1f2937', marginBottom: '8px' }}>Proposal Requests</h1>
          <p style={{ color: '#6b7280', fontSize: '14px' }}>Capture and manage potential client projects</p>
        </div>
        <button
          onClick={() => navigate('/proposals/new')}
          style={{
            padding: '12px 20px',
            background: '#2B7FE0',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            fontSize: '14px',
            fontWeight: '500',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            transition: 'background 0.2s'
          }}
          onMouseOver={e => e.target.style.background = '#2563eb'}
          onMouseOut={e => e.target.style.background = '#2B7FE0'}
        >
          <PlusIcon style={{ width: 18, height: 18 }} /> New Proposal
        </button>
      </div>

      {/* Status Filter Tabs */}
      <div style={{ marginBottom: '24px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
        {[
          { value: 'all', label: 'All' },
          { value: 'draft', label: 'Drafts' },
          { value: 'under_review', label: 'Under Review' },
          { value: 'approved', label: 'Approved' },
          { value: 'rejected', label: 'Rejected' },
          { value: 'converted', label: 'Converted' }
        ].map(status => (
          <button
            key={status.value}
            onClick={() => setStatusFilter(status.value)}
            style={{
              padding: '8px 16px',
              border: statusFilter === status.value ? 'none' : '1px solid #d1d5db',
              borderRadius: '6px',
              background: statusFilter === status.value ? '#2B7FE0' : 'white',
              color: statusFilter === status.value ? 'white' : '#6b7280',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
          >
            {status.label} ({statusCounts[status.value]})
          </button>
        ))}
      </div>

      {/* Search and Content */}
      <div style={{ background: 'white', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
        {/* Search Bar */}
        <div style={{ padding: '20px', borderBottom: '1px solid #e5e7eb' }}>
          <input
            type="text"
            placeholder="🔍 Search proposals by client name or description..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: '100%',
              padding: '12px',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '14px',
              outline: 'none'
            }}
          />
        </div>

        {/* Proposals Table */}
        <div style={{ padding: '20px' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
              Loading proposals...
            </div>
          ) : filteredProposals.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
              {searchTerm ? 'No proposals match your search' : 'No proposals found'}
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                  <th style={{ textAlign: 'left', padding: '12px', color: '#6b7280', fontWeight: '600', fontSize: '14px' }}>Client</th>
                  <th style={{ textAlign: 'left', padding: '12px', color: '#6b7280', fontWeight: '600', fontSize: '14px' }}>Project Description</th>
                  <th style={{ textAlign: 'left', padding: '12px', color: '#6b7280', fontWeight: '600', fontSize: '14px' }}>Est. Cost</th>
                  <th style={{ textAlign: 'left', padding: '12px', color: '#6b7280', fontWeight: '600', fontSize: '14px' }}>Status</th>
                  <th style={{ textAlign: 'left', padding: '12px', color: '#6b7280', fontWeight: '600', fontSize: '14px' }}>Submitted</th>
                  <th style={{ textAlign: 'center', padding: '12px', color: '#6b7280', fontWeight: '600', fontSize: '14px' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredProposals.map(proposal => (
                  <tr key={proposal.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={{ padding: '16px 12px' }}>
                      <div style={{ fontWeight: '500', color: '#1f2937' }}>{proposal.client_name}</div>
                      {proposal.client_phone && (
                        <div style={{ fontSize: '12px', color: '#6b7280' }}>{proposal.client_phone}</div>
                      )}
                    </td>
                    <td style={{ padding: '16px 12px', color: '#6b7280', fontSize: '14px', maxWidth: '300px' }}>
                      {proposal.project_description?.substring(0, 80)}{proposal.project_description?.length > 80 ? '...' : ''}
                    </td>
                    <td style={{ padding: '16px 12px', color: '#1f2937', fontWeight: '500' }}>
                      {proposal.estimated_cost ? `$${parseFloat(proposal.estimated_cost).toLocaleString()}` : '—'}
                    </td>
                    <td style={{ padding: '16px 12px' }}>
                      {getStatusBadge(proposal.status)}
                    </td>
                    <td style={{ padding: '16px 12px', color: '#6b7280', fontSize: '14px' }}>
                      {new Date(proposal.submitted_at).toLocaleDateString()}
                    </td>
                    <td style={{ padding: '16px 12px', textAlign: 'center' }}>
                      <button
                        onClick={() => navigate(`/proposals/${proposal.id}`)}
                        style={{
                          padding: '8px',
                          background: 'none',
                          color: '#2B7FE0',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                        title="View details"
                      >
                        <EyeIcon style={{ width: 18, height: 18 }} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default Proposals;